import { useState } from "react";
import { motion } from "framer-motion";
import { Expand, PawPrint } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { IMAGES } from "@/data";
import { Reveal } from "@/components/Reveal";

const GALLERY = [
  { src: IMAGES.dusk, label: "Blue hour, string lights", alt: "Cottages glowing under string lights at dusk", span: "md:col-span-7 md:row-span-2", h: "h-[300px] md:h-full" },
  { src: IMAGES.dog, label: "Meet our resident greeter", alt: "The resident dog at Vistara", span: "md:col-span-5", h: "h-[300px] md:h-[360px]", greeter: true },
  { src: IMAGES.ridge, label: "Cottage rows on the hill", alt: "Red-roofed cottage row with misty mountains behind at Vistara", span: "md:col-span-5", h: "h-[260px] md:h-[300px]" },
  { src: IMAGES.valley, label: "The valley, breathing", alt: "Mist rolling over the green valley and ridgelines near Vistara", span: "md:col-span-7", h: "h-[300px] md:h-[360px]" },
  { src: IMAGES.walkway, label: "Verandah walks", alt: "Red-brick cottage verandahs along a stone-walled path", span: "md:col-span-5", h: "h-[300px] md:h-[360px]" },
  { src: IMAGES.steps, label: "Red steps, bamboo shade", alt: "Red-oxide steps leading up to cottages backed by bamboo groves", span: "md:col-span-12", h: "h-[300px] md:h-[440px]" },
];

export const Experience = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="moments" data-testid="moments-section" className="bg-cream py-24 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta mb-6">
                (Moments at Vistara)
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-charcoal">
                Dusk on the deck,
                <span className="italic text-mist"> steps to nowhere.</span>
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[minmax(0,1fr)] gap-4 md:gap-6">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={i * 0.08} className={g.span}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(g)}
                className={`group relative w-full ${g.h} overflow-hidden rounded-md block`}
              >
                <img
                  src={g.src}
                  alt={g.alt || g.label}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />

                {g.greeter && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="absolute top-4 right-4 flex items-center gap-2 bg-amber text-charcoal text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-sm"
                  >
                    <PawPrint size={13} strokeWidth={2} /> Resident greeter
                  </motion.div>
                )}

                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <span className="font-serif text-cream text-xl md:text-2xl text-left">{g.label}</span>
                  <span className="shrink-0 w-9 h-9 rounded-full bg-cream/20 backdrop-blur-md flex items-center justify-center text-cream group-hover:bg-terracotta transition-colors duration-300">
                    <Expand size={15} strokeWidth={1.5} />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="lightbox"
          className="max-w-5xl p-0 border-none bg-charcoal overflow-hidden"
        >
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.label}</DialogTitle>
              <img src={active.src} alt={active.label} className="w-full max-h-[80vh] object-contain" />
              <p className="font-serif italic text-cream text-lg px-6 py-4">{active.label}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
